import React from "react";
import PropTypes from "prop-types";

import Captions from "core/components/Captions";
import VideoPreview from "core/components/VideoPreview";
import VideoLoading from "core/components/VideoLoading";
import PlayerPause from "core/components/PlayerPause";
import PlayerHeader from "core/components/PlayerHeader";
import PlayerFooter from "core/components/PlayerFooter";
import BigPlayButton from "core/components/BigPlayButton";
import Shortcut from "core/components/Shortcut";
import { createEvent } from "core/utils/tracker";
import useIdleRef from "core/hooks/useIdleRef";

// Длительность "коротких" роликов (В секундах) - 15 минут
const SHORT_VIDEO_DURATION = 60 * 15;

// Интервал показа инстрим рекламы для "коротких" видео (в секундах) - 2 мин
const SHORT_VIDEO_AD_IMPRESSION_INTERVAL = 60 * 2;

// Интервал показа инстрим рекламы для не "коротких" видео (в секундах) - 9 мин
const LONG_VIDEO_AD_IMPRESSION_INTERVAL = 60 * 9;

// Интервал показа мидролов (в секундах) - 15 мин
const MIDROLL_IMPRESSION_INTERVAL = 60 * 15;

// Интервал первого показа инстрим рекламы (в секундах) - 15 сек
const FIRST_AD_IMPRESSION_INTERVAL = 15;

import SponsorPlayerLogo from "core/components/SponsorPlayerLogo";
import SponsorPlayerButton from "core/components/SponsorPlayerButton";
import SponsorMidAdv from "core/components/SponsorMidAdv";
import SponsorPauseAdv from "core/components/SponsorPauseAdv";
import SponsorPostAdv from "core/components/SponsorPostAdv";
import SponsorInStreamBanner from "core/components/SponsorInStreamBanner";
import SponsorCornerBanner from "core/components/SponsorCornerBanner";
import SponsorLiveAdv from "core/components/SponsorLiveAdv";
import PlayerInView from "core/components/PlayerInView";

export default function VideoScreenContent({
  videoElement,
  videoState,
  videoActions,
  relatedContent,
  chooseCameraButton,
  handleSeekEnd,
  handleSeek,
  handleSeekStart,
  previewInStreamBranding,
  previewInStreamBanner,
  previewPlayerButton,
  previewPlayerLogo,
  seekValue,
  isSeeking,
  inStreamBannerHeight,
  setInStreamBannerHeight,
  cornerBannerHeight,
  setCornerBannerHeight,
  platformId,
  videoRef,
  sponsorCornerBannerPlace,
  sponsorPlayerLogoPlace,
  sponsorPlayerButtonPlace,
  sponsorInStreamBannerPlace,
  sponsorVideoPlace,
  embedId,
  autoPause,
  fullscreenTarget,
  autoPlay,
  content,
  muted,
  pause,
  hideTitle,
  loop,
  hideShare,
  hideLogo,
  video,
  start,
  setPause,
  finish,
  nextPlaylistContentButton,
}) {
  const idle = useIdleRef({ ref: videoRef });
  const visible = !idle;

  const onClickBigPlay = () => {
    videoActions.togglePlayUser();
    if (videoState.status === "playing") {
      createEvent("video", "pause");
    } else {
      createEvent("video", "play");
    }
  };

  const onClickPlay = () => {
    videoActions.togglePlayUser();
    createEvent("video", "play");
  };

  const onClickPause = () => {
    videoActions.togglePlayUser();
    createEvent("video", "pause");
  };

  const onFinishPostroll = () => {
    if (loop) {
      videoActions.play();
    }
  };

  const onChangeInView = (inView) => {
    if (autoPause) {
      if (inView) {
        if (!videoState.isUserPause) {
          videoActions.play();
        }
      } else {
        videoActions.pause();
      }
    }
  };

  let playerHeaderVisible = false;
  let playerFooterVisible = false;

  if (isSeeking) {
    playerFooterVisible = true;
  }

  if (!cornerBannerHeight && !inStreamBannerHeight) {
    if (pause) {
      playerHeaderVisible = true;
    } else {
      if (hideTitle) {
        if (visible || videoState.isUserPause) {
          playerHeaderVisible = true;
          playerFooterVisible = true;
        }
      } else {
        if (videoState.isUserPause) {
          if (visible) {
            playerHeaderVisible = true;
            playerFooterVisible = true;
          }
        } else {
          if (visible) {
            playerHeaderVisible = true;
            playerFooterVisible = true;
          }
        }
      }
    }
  }

  const sponsorPlayerLogoVisible = !(
    cornerBannerHeight > 0 || inStreamBannerHeight > 0
  );

  const sponsorPlayerButtonVisible = !(
    cornerBannerHeight > 0 || inStreamBannerHeight > 0
  );

  const btn =
    nextPlaylistContentButton &&
    nextPlaylistContentButton({ videoState, visible: playerFooterVisible });

  return (
    <>
      <PlayerInView
        cornerBannerHeight={cornerBannerHeight}
        inStreamBannerHeight={inStreamBannerHeight}
        onChange={onChangeInView}
      >
        {videoElement}

        <VideoPreview
          visible={isSeeking}
          videoPreviews={video.videoPreviews}
          seekValue={seekValue}
        />

        <VideoLoading videoState={videoState} />

        <Shortcut videoActions={videoActions} videoState={videoState} />

        {!hideTitle && (videoState.isUserPause || videoState.isEnded) ? (
          <PlayerPause
            title={content.title}
            contentUrl={content.shareUrl}
            relatedContent={relatedContent}
            onClickPlay={onClickBigPlay}
            setVisible={setPause}
            visible={pause}
            hideLogo={hideLogo || content.hideLogo}
          />
        ) : (
          <>
            {sponsorPlayerLogoVisible &&
              sponsorPlayerButtonVisible &&
              playerFooterVisible && (
                <BigPlayButton
                  onClick={onClickBigPlay}
                  videoState={videoState}
                />
              )}
          </>
        )}

        <PlayerHeader
          content={content}
          position="absolute"
          visible={playerHeaderVisible}
          tracker
        />

        <PlayerFooter
          position="absolute"
          content={content}
          videoPreviews={video.videoPreviews}
          visible={playerFooterVisible}
          videoElement={videoElement}
          videoState={videoState}
          videoActions={videoActions}
          onClickPlay={onClickPlay}
          onClickPause={onClickPause}
          onSeekStart={handleSeekStart}
          onSeek={handleSeek}
          onSeekEnd={handleSeekEnd}
          hideShare={hideShare}
          hideLogo={hideLogo}
          fullscreenTarget={fullscreenTarget}
          disableSeekBarPopup={!hideLogo && videoState.isEnded && !loop}
          chooseCameraButton={chooseCameraButton}
          hlsUrl={video.playlist}
          start={start}
          finish={finish}
        />

        {btn}

        {(content.isAdvEnabled || previewPlayerLogo) &&
          sponsorPlayerLogoPlace && (
            <SponsorPlayerLogo
              embedId={embedId}
              previewBanner={previewPlayerLogo}
              platformId={platformId}
              contentId={content.id}
              rightholderId={content.rightholder.id}
              place={sponsorPlayerLogoPlace}
              footerVisible={playerFooterVisible}
              visible={sponsorPlayerLogoVisible}
            />
          )}

        {(content.isAdvEnabled || previewPlayerButton) &&
          !content.widget &&
          sponsorPlayerButtonPlace && (
            <SponsorPlayerButton
              embedId={embedId}
              previewBanner={previewPlayerButton}
              platformId={platformId}
              contentId={content.id}
              rightholderId={content.rightholder.id}
              place={sponsorPlayerButtonPlace}
              footerVisible={playerFooterVisible}
              visible={sponsorPlayerButtonVisible}
            />
          )}

        <Captions
          cues={videoState.activeCues}
          visible={visible || videoState.isUserPause}
        />
      </PlayerInView>

      {previewInStreamBanner && (
        <SponsorInStreamBanner
          previewBanner={previewInStreamBanner}
          setInStreamBannerHeight={setInStreamBannerHeight}
        />
      )}

      {previewInStreamBranding && (
        <SponsorCornerBanner
          previewBanner={previewInStreamBranding}
          setCornerBannerHeight={setCornerBannerHeight}
        />
      )}

      {content.isAdvEnabled && (
        <>
          {sponsorVideoPlace && (
            <SponsorLiveAdv
              embedId={embedId}
              platformId={platformId}
              fullscreenTarget={fullscreenTarget}
              videoActions={videoActions}
              videoState={videoState}
              content={content}
              rightholderId={content.rightholder.id}
              sponsorVideoPlace={sponsorVideoPlace}
              muted={muted || autoPlay}
              impressionInterval={MIDROLL_IMPRESSION_INTERVAL}
              disabled={!sponsorPlayerLogoVisible || !sponsorPlayerLogoVisible}
            />
          )}

          {sponsorCornerBannerPlace && (
            <SponsorMidAdv
              embedId={embedId}
              firstImpressionInterval={FIRST_AD_IMPRESSION_INTERVAL}
              impressionInterval={
                videoState.duration > SHORT_VIDEO_DURATION
                  ? LONG_VIDEO_AD_IMPRESSION_INTERVAL
                  : SHORT_VIDEO_AD_IMPRESSION_INTERVAL
              }
              platformId={platformId}
              fullscreenTarget={fullscreenTarget}
              videoActions={videoActions}
              videoState={videoState}
              contentId={content.id}
              rightholderId={content.rightholder.id}
              sponsorInStreamBannerPlace={sponsorInStreamBannerPlace}
              sponsorCornerBannerPlace={sponsorCornerBannerPlace}
              setInStreamBannerHeight={setInStreamBannerHeight}
              setCornerBannerHeight={setCornerBannerHeight}
            />
          )}

          {sponsorVideoPlace && (
            <SponsorPauseAdv
              embedId={embedId}
              platformId={platformId}
              fullscreenTarget={fullscreenTarget}
              videoState={videoState}
              content={content}
              rightholderId={content.rightholder.id}
              sponsorVideoPlace={sponsorVideoPlace}
              muted={muted}
            />
          )}

          {sponsorVideoPlace && (
            <SponsorPostAdv
              embedId={embedId}
              platformId={platformId}
              fullscreenTarget={fullscreenTarget}
              videoState={videoState}
              content={content}
              rightholderId={content.rightholder.id}
              sponsorVideoPlace={sponsorVideoPlace}
              muted={muted || autoPlay}
              onFinish={onFinishPostroll}
            />
          )}
        </>
      )}
    </>
  );
}

VideoScreenContent.propTypes = {
  fullscreenTarget: PropTypes.string,
  muted: PropTypes.bool,
  loop: PropTypes.bool,
  hideTitle: PropTypes.bool,
  hideShare: PropTypes.bool,
  hideLogo: PropTypes.bool,
  autoPlay: PropTypes.bool,
  autoPause: PropTypes.bool,
  content: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    shareUrl: PropTypes.string.isRequired,
    widget: PropTypes.object,

    relatedContent: PropTypes.arrayOf(PropTypes.object),
    hideRelatedContent: PropTypes.bool,
    isAdvEnabled: PropTypes.bool,
    rightholder: PropTypes.shape({
      id: PropTypes.string,
      hideRelatedContent: PropTypes.bool,
    }),
    sport: PropTypes.shape({
      title: PropTypes.string,
      slug: PropTypes.string,
      shareUrl: PropTypes.string,
    }),
    hideLogo: PropTypes.bool,
  }),
  video: PropTypes.shape({
    id: PropTypes.string.isRequired,
    playlist: PropTypes.string.isRequired,
    duration: PropTypes.number,
    viewerVideoPosition: PropTypes.number,
    videoPreviews: PropTypes.object,
  }),

  start: PropTypes.number,
  finish: PropTypes.number,
  sponsorPlayerLogoPlace: PropTypes.string,
  sponsorPlayerButtonPlace: PropTypes.string,
  sponsorInStreamBannerPlace: PropTypes.string,
  sponsorCornerBannerPlace: PropTypes.string,
  sponsorVideoPlace: PropTypes.string,
  platformId: PropTypes.string,
  embedId: PropTypes.string,

  videoElement: PropTypes.object,
  videoState: PropTypes.object,
  videoActions: PropTypes.object,
  relatedContent: PropTypes.object,
  chooseCameraButton: PropTypes.object,
  handleSeekEnd: PropTypes.func,
  handleSeek: PropTypes.func,
  handleSeekStart: PropTypes.func,
  previewInStreamBranding: PropTypes.object,
  previewInStreamBanner: PropTypes.object,
  previewPlayerButton: PropTypes.object,
  previewPlayerLogo: PropTypes.object,
  seekValue: PropTypes.number,
  isSeeking: PropTypes.bool,
  inStreamBannerHeight: PropTypes.object,
  setInStreamBannerHeight: PropTypes.func,
  cornerBannerHeight: PropTypes.object,
  setCornerBannerHeight: PropTypes.func,

  videoRef: PropTypes.object,
  pause: PropTypes.bool,
  setPause: PropTypes.func,

  nextPlaylistContentButton: PropTypes.func,
};
